import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { fetchData, postData } from 'helpers/fetch'
import { addClass, select, selectAll, removeClass } from 'helpers/dom'
import { getCookie } from '@convertdigital/gamma-cookie'

const selectors = {
  CART_COUNT_SELECTOR: '#CartCount',
  MINI_CART_CONTENT_CLASS: '.js-mini-cart-content',
  MAX_CART_ITEM_QUANTITY: 1000000
}

const cartError = async (response) => {
  const error = await response.json()

  globalEmitter.emit(emitterProps.notice.global, {
    type: 'error',
    content: error.description
  })
  throw new Error(`${response.status}: ${error.description}`)
}

const getNativeCart = async () => {
  const cart = await fetchData('/cart.js', cartError)

  return cart
}

const getCart = async () => {
  const cart = await fetchData('/cart?view=ajax', cartError)

  return cart
}

const getLineItem = (id, properties, quantity, sellingPlan) => {
  const data = {
    items: [
      {
        id,
        properties,
        quantity,
        selling_plan: sellingPlan
      }
    ]
  }

  return data
}

const addCartItem = async (id, properties = {}, quantity = 1, sellingPlan = null) => {
  const lineItem = getLineItem(id, properties, quantity, sellingPlan)

  return postData('/cart/add.js', lineItem, 'json', cartError).then((item) => item)
}

const addMultipleCartItems = async (items) => postData('/cart/add.js', items, 'json', cartError).then((response) => response)

const updateCartItem = async (id, quantity) => postData('/cart/change.js', {
  id,
  quantity
}, 'json', cartError).then((cart) => {
  cart && globalEmitter.emit(emitterProps.cart.change, cart)

  return cart
})

const changeQuantity = async (id, quantity) => postData('/cart/change.js', {
  id,
  quantity
}, 'json', cartError).then((cart) => cart)

const removeCartItem = async (id) => postData('/cart/change.js', {
  id,
  quantity: 0
}, 'json', cartError).then((cart) => {
  if (cart) {
    globalEmitter.emit(emitterProps.cart.change, cart)
  }

  return cart
})

const updateMultipleCartItems = async (payload) => postData('/cart/update.js', payload, 'json', cartError).then((items) => items)

const clearCart = async () => postData('/cart/clear.js', {}, 'json', cartError).then((cart) => {
  globalEmitter.emit(emitterProps.cart.clear, cart)

  return cart
})

const updateNoteCart = (noteCart) => postData(`${window.Shopify.routes.root}cart/update.js`, { note: noteCart }, 'json', cartError).then((cart) => cart)


const reRenderCartCount = (count) => {
  const els = selectAll(selectors.CART_COUNT_SELECTOR, document.body)

  if (!els) return

  els.forEach((el) => {
    const text = el.hasAttribute('data-type') ? `(${count})` : count

    // eslint-disable-next-line no-param-reassign
    el.innerText = count === 0 ? '' : text

    if (count === 0) {
      addClass('hidden', el)
    } else {
      removeClass('hidden', el)
      addClass('inline-block', el)
    }
  })
}

const updateCart = async () => {
  const newCart = await getCart()

  if (newCart) {
    reRenderCartCount(newCart.item_count)
  }

  return newCart
}

const forceReloadItem = async (item) => {
  const currentQty = item.quantity
  const nextQty = item.quantity + 1

  await changeQuantity(item.key, nextQty)

  const cart = await changeQuantity(item.key, currentQty)

  return cart
}

const openMiniCart = () => {
  const el = select('cart-slideout modal-dialog', document.body)

  if (el) {
    el.show()

    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)

    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const getCartProperties = (items) => {
  if (!items || Object.keys(items).length === 0) return []

  return Object.entries(items).map(([key, value]) => ({
    name: key,
    key: value
  }))
}

const getDisableChangeQuantity = (properties) => properties && properties?._disable_quantity === 'true'

const getMaxQuantity = (cartItem, cartItems) => {
  if (!cartItem.inventory_management || cartItem.inventory_policy === 'continue') return selectors.MAX_CART_ITEM_QUANTITY

  const currentItems = cartItems.filter(item => item.id === cartItem.id)

  if (!currentItems) return cartItem.inventory_quantity

  return cartItem.inventory_quantity - (currentItems.reduce((acc, item) => acc + item.quantity, 0) - cartItem.quantity)
}

const getMaxQuantityByVariant = (cartItems, variant) => {
  if (!variant.inventory_management || variant.inventory_policy === 'continue') return selectors.MAX_CART_ITEM_QUANTITY

  const currentItems = cartItems.filter(item => item.id === variant.id)

  if (!currentItems) return variant.inventory_quantity

  return variant.inventory_quantity - currentItems.reduce((acc, item) => acc + item.quantity, 0)
}

const clearBundlesChildCart = async (itemsCart, productId, productBundles) => {
  const bundlesChild = productBundles?.find((bundle) => bundle.id === productId)?.bundlesChild || []

  if (!bundlesChild.length) return

  const productsChildInCart = itemsCart.filter((cartItem) =>
    bundlesChild.some((bundle) => cartItem.variant_id === bundle.variant_id)
  )

  if (!productsChildInCart.length) return

  const products = {}

  productsChildInCart.forEach((product) => {
    products[product.key] = 0
  })

  const body = {
    updates: products
  }

  try {
    await updateMultipleCartItems(body)
  } catch (error) {
    console.warn('Failed to clear bundle child items:', error)
  }
}

const noteGwpGiftRemoval = async () => {
  let orderNote = ''
  const cart = window.GM_STATE.cart?.initCart || {}
  const oldCartNote = cart?.note || ''
  const hasGwpAutoAddInCart = cart.items?.some((item) => item.discounts.length && item.discounts.some((discount) => discount.title?.trim()?.toLowerCase() === 'gift100'))
  const hasGwpAutoAdd = getCookie('GWPAutoAddRemoved') === 'true' && !hasGwpAutoAddInCart
  const hasGwpNotSold = getCookie('GWPNotSoldRemoved') === 'true'

  if (hasGwpAutoAdd && hasGwpNotSold) {
    orderNote = 'User removed GWP Auto Add and GWP Not Sold Online products'
  } else if (hasGwpAutoAdd) {
    orderNote = 'User removed GWP Auto Add product'
  } else if (hasGwpNotSold) {
    orderNote = 'User removed GWP Not Sold Online product'
  }

  oldCartNote !== orderNote && await updateNoteCart(orderNote)
}

const updateAttributesCart = (attributesCart) => postData(`${window.Shopify.routes.root}cart/update.js`, { attributes: attributesCart }, 'json', cartError).then((cart) => cart)

export {
  getNativeCart,
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  updateCart,
  openMiniCart,
  getCartProperties,
  getDisableChangeQuantity,
  forceReloadItem,
  getMaxQuantity,
  reRenderCartCount,
  getMaxQuantityByVariant,
  updateMultipleCartItems,
  changeQuantity,
  addMultipleCartItems,
  clearBundlesChildCart,
  updateNoteCart,
  noteGwpGiftRemoval,
  updateAttributesCart
}
